import React, { useState, useEffect, useCallback } from "react";
import {
  nomFichierDocument,
  nomFichierResume,
} from "../../Utils/documentNaming";
import {
  Typography,
  Card,
  CardContent,
  Button,
  makeStyles,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Grid,
} from "@material-ui/core";
import {
  GetApp,
  PictureAsPdf,
  Description,
  LocalHospital,
  CreditCard,
  InsertDriveFile,
  Person,
  Event,
  AccessTime,
  CheckCircle,
  Cake,
} from "@material-ui/icons";
import moment from "moment";
import ExpressAPI from "../../Utils/ExpressAPI";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useFirebase } from "../../Firebase/FirebaseContext";
// Le statut d'un dossier se lit au meme endroit qu'au secretariat : ces
// deux ecrans avaient deja diverge une fois, sur le nommage des fichiers.
import {
  STATUT_TRAITE,
  STATUT_A_VERIFIER,
  statutDossier,
} from "../../Utils/statutDossier";

export default function AppointmentCard({ appointment, onMarkSeen }) {
  const classes = useStyles();
  const { firebase } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  const statut = statutDossier(appointment);
  const isSeen = statut === STATUT_TRAITE;
  const aVerifier = statut === STATUT_A_VERIFIER;

  const markSeenInFirestore = useCallback(async () => {
    try {
      const docRef = doc(firebase.db, "appointements", appointment.id);
      // On pose aussi le statut, pour que le secretariat et cette tuile
      // racontent la meme histoire du meme dossier.
      await updateDoc(docRef, { seen: true, statut: STATUT_TRAITE, seenAt: serverTimestamp() });
      if (onMarkSeen) onMarkSeen(appointment.id);
    } catch (e) {
      console.log("markSeen error:", e);
    }
  }, [firebase.db, appointment.id, onMarkSeen]);

  /**
   * ─────────────────────────────────────────────────────────────────────────
   * LE RESUME DU RENDEZ-VOUS : DEUX GESTES, UN SEUL STATUT
   * ─────────────────────────────────────────────────────────────────────────
   *
   * Le 07/09, le telechargement avait ete remplace par l'ouverture dans un
   * onglet (« il faudrait que dans le web tu remettes simplement l'ouverture
   * du PDF dans Chrome et non le telechargement »). Le 08/09, Nicolas veut les
   * DEUX : « ajoute un bouton "Afficher le PDF" et "Telecharger le PDF", les 2
   * passent la tuile en Traite ».
   *
   * CE QUE « TRAITE » SIGNIFIE, ET CE QUE CELA COUTE. Avant le 07/09, le
   * dossier n'etait inscrit « traite » qu'apres une ecriture confirmee par le
   * poste : une boite d'enregistrement annulee le laissait a traiter. Un
   * onglet n'a pas d'equivalent — le navigateur ne dit jamais si la personne a
   * garde le fichier. « Traite » veut donc dire « le resume est parti », ce
   * qui reste vrai et verifiable, et non « enregistre », qu'on ne saurait plus
   * prouver. Nicolas a tranche dans ce sens, deux fois.
   *
   * Les deux boutons appliquent la MEME regle au meme instant. Deux boutons
   * voisins qui marqueraient « traite » a deux moments differents seraient une
   * regle qu'aucune secretaire ne pourrait retenir.
   *
   * Le nommage des fichiers reste celui de `Utils/documentNaming`, partage
   * avec le secretariat depuis le 28/08.
   */
  /** Le nom de fichier du resume, meme regle que partout ailleurs. */
  const nomDuResume = () => {
    const timestamp =
      appointment.ended_at?.seconds ||
      appointment.started_at?.seconds ||
      Math.floor(Date.now() / 1000);
    return nomFichierResume({
      barcode: appointment.barcode,
      date: moment.unix(timestamp).format("DD_MM_YYYY"),
      firstName: appointment.name,
      maritalName: appointment.maritalName,
      lastName: appointment.lastName,
    });
  };

  /**
   * Le resume, rapatrie du serveur. Rend `null` si le PDF revient vide.
   *
   * Un PDF de zero octet n'est pas un PDF : on ne marque rien, on n'ouvre
   * rien, on n'enregistre rien. C'est le seul cas ou ne rien faire vaut mieux
   * que faire quelque chose.
   */
  const recupererResume = async () => {
    // Sans identifiant, la route partirait en `pdf/getAppointment/undefined`
    // et le serveur repondrait 500. On ne touche pas au reseau, on le dit a la
    // secretaire, et on ne marque RIEN : un dossier dont on n'a pas pu sortir
    // le resume doit rester a traiter. La cause est en amont (une tuile
    // construite sans `id`), d'ou le « rechargez la page » : le seul geste
    // qui la remet d'aplomb.
    if (!appointment.id) {
      alert(
        "Le résumé de ce dossier n'est pas disponible, rechargez la page."
      );
      return null;
    }

    const res = await ExpressAPI(
      "GET",
      `pdf/getAppointment/${appointment.id}`,
      [],
      "blob"
    );
    const file = new Blob([res.data], { type: "application/pdf" });
    return file.size ? file : null;
  };

  /** « Traite », et la trace locale qui va avec. Les deux boutons y passent. */
  const marquerTraite = () => {
    localStorage.setItem(appointment.id, 1);
    markSeenInFirestore();
  };

  /**
   * OUVRIR le resume dans un onglet.
   *
   * L'onglet est ouvert AVANT l'appel reseau, tant qu'on est encore dans le
   * clic : un `window.open` apres un `await` est bloque par Chrome.
   *
   * Le nom de fichier ne sert qu'au repli, quand l'onglet est refuse. Un PDF
   * enregistre depuis la visionneuse de Chrome, lui, ne porte pas ce nom :
   * c'est « Telecharger le PDF », a cote, qui garantit la syntaxe du SIL.
   */
  const ouvrirResumePdf = async () => {
    const onglet = window.open("", "_blank");
    try {
      setLoading(true);
      const fileName = nomDuResume();
      const file = await recupererResume();
      if (!file) {
        // PDF vide, ou dossier sans identifiant : dans les deux cas le resume
        // n'est pas parti, donc rien n'est marque « traite ».
        if (onglet) onglet.close();
        setLoading(false);
        return;
      }

      const fileURL = URL.createObjectURL(file);
      if (onglet) {
        onglet.location = fileURL;
      } else {
        // Onglet refuse par le navigateur : plutot que de ne rien faire sous
        // le doigt de la secretaire, on retombe sur l'enregistrement.
        const link = document.createElement("a");
        link.href = fileURL;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }

      setLoading(false);
      marquerTraite();
    } catch (e) {
      if (onglet) onglet.close();
      console.log(e);
      setLoading(false);
    }
  };

  /**
   * ENREGISTRER le resume sur le poste.
   *
   * Retour du 08/09 : « ajoute un bouton "Afficher le PDF" et "Telecharger le
   * PDF", les 2 passent la tuile en Traite ; le premier bouton ouvre le PDF
   * dans une autre page, le deuxieme telecharge le PDF sur l'ordinateur. »
   *
   * Le 07/09, le telechargement avait ete retire au profit de la seule
   * ouverture. Il revient a cote d'elle, et non a sa place.
   *
   * « TRAITE » VEUT TOUJOURS DIRE « OUVERT », PAS « ENREGISTRE », et il faut
   * le redire ici : Nicolas a tranche le 07/09 pour que l'ouverture suffise
   * (« des que l'on clic sur Ouvrir le PDF alors on considere le dossier comme
   * traite »). Ce bouton-ci pourrait, lui, attendre une ecriture confirmee —
   * mais deux boutons cote a cote qui ne marqueraient pas « traite » au meme
   * moment seraient une regle impossible a expliquer a une secretaire. Ils
   * marquent donc tous les deux des que le PDF est parti, et c'est ce qu'il a
   * demande : « les 2 passent la tuile en Traite ».
   */
  const telechargerResumePdf = async () => {
    try {
      setLoading(true);
      const fileName = nomDuResume();
      const file = await recupererResume();
      if (!file) {
        setLoading(false);
        return;
      }

      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(fileURL), 60000);

      setLoading(false);
      marquerTraite();
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  // La reprise apres la fenetre « une collegue s'en est deja occupe » rouvre
  // le resume : c'est le geste le plus courant, et le moins engageant.
  const downloadPDF = ouvrirResumePdf;
  const downloadPDFDirectly = ouvrirResumePdf;

  const handleConfirmDownload = () => {
    setOpenDialog(false);
    if (pendingDownload) {
      if (pendingDownload.type === "pdf") {
        downloadPDFDirectly();
      } else if (pendingDownload.type === "firebase") {
        downloadDocumentFromFirebaseStorageDirectly(
          pendingDownload.path,
          pendingDownload.infos
        );
      }
      setPendingDownload(null);
    }
  };

  const handleCancelDownload = () => {
    setOpenDialog(false);
    setPendingDownload(null);
  };


  const downloadDocumentFromFirebaseStorage = async (path, infos) => {
    try {
      setLoading(true);

      const storage = getStorage();
      const pathReference = ref(storage, path);

      getDownloadURL(pathReference)
        .then((url) => {
          const xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.onload = (event) => {
            const blob = xhr.response;
            const fileURL = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = fileURL;
            const newName = getFormattedName(infos);
            link.setAttribute("download", newName);

            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setLoading(false);
            localStorage.setItem(appointment.id, 1);
          };
          xhr.open("GET", url);
          xhr.send();
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const downloadDocumentFromFirebaseStorageDirectly = async (path, infos) => {
    try {
      setLoading(true);

      const storage = getStorage();
      const pathReference = ref(storage, path);

      getDownloadURL(pathReference)
        .then((url) => {
          const xhr = new XMLHttpRequest();
          xhr.responseType = "blob";
          xhr.onload = (event) => {
            const blob = xhr.response;
            const fileURL = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = fileURL;
            const newName = getFormattedName(infos);
            link.setAttribute("download", newName);

            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setLoading(false);
            localStorage.setItem(appointment.id, 1);
          };
          xhr.open("GET", url);
          xhr.send();
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  // La regle de nommage est commune aux deux ecrans (Utils/documentNaming) :
  // c'est celle-ci qui fait foi, et le secretariat l'applique desormais aussi.
  const getFormattedName = (infos) =>
    nomFichierDocument({
      barcode: appointment.barcode,
      jour: infos.day,
      type: infos.type,
      firstName: infos.name,
      maritalName: infos.maritalName,
      lastName: infos.lastName,
    });

  const getDocDay = () =>
    appointment.technician_came_at?.seconds
      ? moment
          .unix(appointment.technician_came_at.seconds)
          .format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

  const docInfos = {
    name: appointment.name,
    lastName: appointment.lastName,
    // getFormattedName privilegie le nom d'usage : encore faut-il le lui passer.
    maritalName: appointment.maritalName,
    day: getDocDay(),
  };

  const documents = [
    { key: "prescription", label: "Ordonnance", type: "ordo1", icon: Description },
    { key: "prescriptiontwo", label: "Ordonnance 2", type: "ordo2", icon: Description },
    { key: "prescriptionthree", label: "Ordonnance 3", type: "ordo3", icon: Description },
    { key: "mutual", label: "Mutuelle", type: "mutuelle", icon: LocalHospital },
    { key: "IDCard", label: "Pièce d'identité", type: "piece-identite", icon: CreditCard },
    { key: "document", label: "Document", type: "document", icon: InsertDriveFile },
  ].filter((doc) => appointment[doc.key]);

  const endedDate = appointment.ended_at?.seconds
    ? moment.unix(appointment.ended_at.seconds).format("DD/MM/YYYY")
    : null;
  const endedTime = appointment.ended_at?.seconds
    ? moment.unix(appointment.ended_at.seconds).format("HH:mm")
    : null;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
      <Card className={`${classes.card} ${isSeen ? classes.cardSeen : ""} ${aVerifier ? classes.cardAVerifier : ""}`}>
        {loading && <LinearProgress className={classes.progress} />}

        <CardContent className={classes.cardContent}>
          {/* Header: Patient name + status badge */}
          <Box className={classes.cardHeader}>
            <Box className={classes.patientInfo}>
              <Box className={classes.avatarCircle}>
                <Person style={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                {/* Nom d'usage en tete quand il existe, nom de naissance en
                    second : la tuile affichait toujours le nom de naissance. */}
                <Typography className={classes.patientName}>
                  {appointment.maritalName || appointment.lastName} {appointment.name}
                </Typography>
                {appointment.maritalName &&
                  appointment.lastName &&
                  appointment.maritalName !== appointment.lastName && (
                    <Typography className={classes.patientBirthName}>
                      né(e) {appointment.lastName}
                    </Typography>
                  )}
                {appointment.birthday && (
                  <Typography className={classes.patientBirthday}>
                    <Cake style={{ fontSize: 13, marginRight: 4, verticalAlign: "middle" }} />
                    {appointment.birthday}
                  </Typography>
                )}
              </Box>
            </Box>
            {isSeen && (
              <Chip
                icon={<CheckCircle style={{ fontSize: 14 }} />}
                label="Traité"
                size="small"
                className={classes.seenChip}
              />
            )}
            {/* Retour du 04/09 : le statut posé au secrétariat doit se voir
                ici aussi. Le violet attire l'oeil sans dire « faute » — ce
                dossier attend un second regard, il n'est pas en erreur. */}
            {aVerifier && (
              <Chip
                label="À vérifier"
                size="small"
                className={classes.aVerifierChip}
              />
            )}
          </Box>

          {/* Date & Time info */}
          <Box className={classes.dateSection}>
            {endedDate && (
              <Box className={classes.dateItem}>
                <Event style={{ fontSize: 15, color: "#6B7280" }} />
                <Typography className={classes.dateText}>{endedDate}</Typography>
              </Box>
            )}
            {endedTime && (
              <Box className={classes.dateItem}>
                <AccessTime style={{ fontSize: 15, color: "#6B7280" }} />
                <Typography className={classes.dateText}>{endedTime}</Typography>
              </Box>
            )}
            {appointment.appointment && (
              <Chip
                label="RDV"
                size="small"
                className={classes.rdvChip}
              />
            )}
          </Box>

          {/* Documents available indicator */}
          {documents.length > 0 && (
            <Box className={classes.docsIndicator}>
              <Typography className={classes.docsLabel}>
                {documents.length} document{documents.length > 1 ? "s" : ""}
              </Typography>
            </Box>
          )}

          {/* Retour du 08/09 : « ajoute un bouton "Afficher le PDF" et
              "Telecharger le PDF", les 2 passent la tuile en Traite ».
              L'affichage est a gauche et rempli : c'est le geste courant. */}
          <Box className={classes.pdfActions}>
            <Button
              variant="contained"
              className={classes.pdfButton}
              onClick={() => ouvrirResumePdf()}
              startIcon={<PictureAsPdf />}
              disabled={loading}
            >
              Afficher le PDF
            </Button>
            <Button
              variant="outlined"
              className={classes.pdfButtonSecondaire}
              onClick={() => telechargerResumePdf()}
              startIcon={<GetApp />}
              disabled={loading}
            >
              Télécharger le PDF
            </Button>
          </Box>

          {/* Document download buttons */}
          {documents.length > 0 && (
            <Box className={classes.docButtons}>
              {documents.map((doc) => {
                const DocIcon = doc.icon;
                return (
                  <Tooltip title={doc.label} key={doc.key} arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.docButton}
                      onClick={() =>
                        downloadDocumentFromFirebaseStorage(
                          appointment[doc.key],
                          { ...docInfos, type: doc.type }
                        )
                      }
                      startIcon={<DocIcon style={{ fontSize: 16 }} />}
                      disabled={loading}
                    >
                      {doc.label}
                    </Button>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </CardContent>

        {/* Dialogue de confirmation */}
        <Dialog
          open={openDialog}
          onClose={handleCancelDownload}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirmation de téléchargement"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Oups, visiblement une collègue s'est déjà occupé de ce dossier,
              voulez vous quand même télécharger le PDF?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDownload} color="primary">
              Annuler
            </Button>
            <Button onClick={handleConfirmDownload} color="primary" autoFocus>
              Télécharger quand même
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Grid>
  );
}

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
    borderRadius: 16,
    transition: "all 0.2s ease",
    border: `1px solid #E5E7EB`,
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)",
      transform: "translateY(-2px)",
      borderColor: theme.palette.primary.light,
    },
  },
  cardSeen: {
    borderLeft: `4px solid #10B981`,
  },
  cardAVerifier: {
    borderLeft: `4px solid #7C3AED`,
  },
  aVerifierChip: {
    backgroundColor: "#f3ecff",
    color: "#5b21b6",
    fontWeight: 700,
    border: "1px solid #d9c7ff",
  },
  progress: {
    height: 3,
    borderRadius: "16px 16px 0 0",
  },
  cardContent: {
    padding: "20px !important",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    flex: 1,
    [theme.breakpoints.down("xs")]: {
      padding: "14px !important",
      gap: 10,
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  patientInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  patientName: {
    fontWeight: 700,
    fontSize: 15,
    color: "#111827",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  patientBirthName: {
    fontSize: 12,
    color: "#6B7280",
    fontStyle: "italic",
    marginTop: 1,
  },
  patientBirthday: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
    display: "flex",
    alignItems: "center",
  },
  seenChip: {
    backgroundColor: "#D1FAE5",
    color: "#065F46",
    fontWeight: 600,
    fontSize: 11,
    height: 24,
    "& .MuiChip-icon": {
      color: "#10B981",
    },
  },
  dateSection: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    flexWrap: "wrap",
  },
  dateItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  dateText: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
  },
  rdvChip: {
    backgroundColor: "#EEF2FF",
    color: "#4338CA",
    fontWeight: 600,
    fontSize: 11,
    height: 22,
    marginLeft: "auto",
  },
  docsIndicator: {
    display: "flex",
    alignItems: "center",
  },
  docsLabel: {
    fontSize: 12,
    color: "#6B7280",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  // Les deux boutons du resume. Cote a cote tant qu'ils tiennent, l'un
  // au-dessus de l'autre sur une tuile etroite : « Telecharger le PDF » ne
  // doit jamais sortir de la carte ni voir son libelle coupe.
  pdfActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  pdfButton: {
    flex: "1 1 130px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    padding: "10px 12px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
  pdfButtonSecondaire: {
    flex: "1 1 130px",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.light,
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    padding: "10px 12px",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
  },
  docButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  docButton: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 500,
    fontSize: 12,
    padding: "6px 12px",
    justifyContent: "flex-start",
    borderColor: "#E5E7EB",
    color: "#374151",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}08`,
      color: theme.palette.primary.main,
    },
  },
}));
